import {
  Box,
  Cell,
  CellText,
  Flex,
  SearchField,
  Select,
  SelectItem,
  Table,
  Text,
  useTable,
} from '@backstage/ui';

import {
  CHECK_RESULT_DISPLAY,
  type CheckResultDisplay,
  FILTERABLE_CHECK_RESULTS,
  toCheckResultDisplay,
} from '../CheckResultBadge/checkResult';

import { CheckResultBadge } from '../CheckResultBadge/CheckResultBadge';
import type { CheckRow } from '../../hooks/useEntityScorecard';
import type { ColumnConfig } from '@backstage/ui';
import { severityRank } from '../../severity';
import { useSeverities } from '../../hooks/useSeverities';
import { useMemo } from 'react';
import { useScorecardsFilter } from '../EntityScorecardsTab/ScorecardsFilterContext';
import styles from './ChecksTable.module.css';

const STATUS_ITEMS: { id: CheckResultDisplay | ''; label: string }[] = [
  { id: '', label: 'All statuses' },
  ...FILTERABLE_CHECK_RESULTS.map(id => ({
    id,
    label: CHECK_RESULT_DISPLAY[id].label,
  })),
];

const statusSortOrder = (row: CheckRow) =>
  CHECK_RESULT_DISPLAY[toCheckResultDisplay(row.snapshot)].sortOrder;

// Search matches the visible label, so "no run" finds checks without a snapshot.
const resultSearchText = (row: CheckRow) =>
  CHECK_RESULT_DISPLAY[toCheckResultDisplay(row.snapshot)].label.toLowerCase();

interface ChecksTableProps {
  rows: CheckRow[];
  isLoading: boolean;
  onRowClick: (row: CheckRow) => void;
}

export const ChecksTable = ({
  rows,
  isLoading,
  onRowClick,
}: ChecksTableProps) => {
  const { activeSeverities, activeCategories } = useScorecardsFilter();
  const { severities } = useSeverities();

  const columnConfig: ColumnConfig<CheckRow>[] = useMemo(
    () => [
      {
        id: 'check',
        label: 'Check',
        isRowHeader: true,
        isSortable: true,
        cell: row => (
          <Cell>
            <button
              className={styles.checkNameButton}
              onClick={() => onRowClick(row)}
            >
              {row.check.name}
            </button>
          </Cell>
        ),
      },
      {
        id: 'category',
        label: 'Category',
        cell: row => <CellText title={row.check.category} />,
      },
      {
        id: 'severity',
        label: 'Severity',
        isSortable: true,
        cell: row => {
          const sv = severities.find(s => s.id === row.check.severity);
          return <CellText title={sv?.label ?? row.check.severity} />;
        },
      },
      {
        id: 'status',
        label: 'Status',
        cell: row => (
          <Cell>
            <CheckResultBadge result={toCheckResultDisplay(row.snapshot)} />
          </Cell>
        ),
        isSortable: true,
      },
    ],
    [onRowClick, severities],
  );

  const filteredRows = useMemo(() => {
    let result = rows;
    if (activeSeverities.size)
      result = result.filter(r => activeSeverities.has(r.check.severity));
    if (activeCategories.size)
      result = result.filter(r => activeCategories.has(r.check.category));
    return result;
  }, [rows, activeSeverities, activeCategories]);

  const { tableProps, filter, search } = useTable<CheckRow, string | undefined>(
    {
      mode: 'complete',
      data: filteredRows,
      searchDebounceMs: 200,
      searchFn: (data, query) => {
        const q = query.toLowerCase();
        return data.filter(row => {
          const sv = severities.find(s => s.id === row.check.severity);
          const sevText = (sv?.label ?? row.check.severity).toLowerCase();
          return (
            row.check.name.toLowerCase().includes(q) ||
            (row.check.description ?? '').toLowerCase().includes(q) ||
            row.check.category.toLowerCase().includes(q) ||
            sevText.includes(q) ||
            resultSearchText(row).includes(q)
          );
        });
      },
      filterFn: (data, status) => {
        if (!status) return data;
        return data.filter(
          row => toCheckResultDisplay(row.snapshot) === status,
        );
      },
      sortFn: (data, { column, direction }) => {
        const sorted = [...data].sort((a, b) => {
          if (column === 'severity') {
            return (
              severityRank(a.check.severity, severities) -
              severityRank(b.check.severity, severities)
            );
          }
          if (column === 'status')
            return statusSortOrder(a) - statusSortOrder(b);
          if (column === 'check')
            return a.check.name.localeCompare(b.check.name);
          return 0;
        });
        return direction === 'descending' ? sorted.reverse() : sorted;
      },
    },
  );

  return (
    <Flex direction="column" gap="4">
      <Flex gap="3" align="center" justify="end">
        <Box>
          <SearchField
            value={search.value}
            onChange={search.onChange}
            aria-label="Search checks"
          />
        </Box>
        <Box shrink={false}>
          <Select
            items={STATUS_ITEMS}
            value={filter.value ?? ''}
            onChange={key => filter.onChange(String(key) || undefined)}
          >
            {item => (
              <SelectItem id={item.id} textValue={item.label}>
                {item.id ? <CheckResultBadge result={item.id} /> : item.label}
              </SelectItem>
            )}
          </Select>
        </Box>
      </Flex>
      <Table
        {...tableProps}
        columnConfig={columnConfig}
        isPending={isLoading}
        emptyState={
          <Text as="p" color="secondary">
            No checks found for this entity.
          </Text>
        }
      />
    </Flex>
  );
};
